import { AsyncPipe, getCurrencySymbol, NgIf, CurrencyPipe, NgFor, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  Invoice,
  InvoiceService,
  MerchantService,
  PaymentService,
  RetailPayment,
  RetailPaymentMethodType,
  RetailRefundReason,
  TransactionReferenceType,
  Transaction,
} from '@galaxy/billing';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectInputOption } from '@vendasta/galaxy/input';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { convertCentsToDollars, Validator } from '@vendasta/shared';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { finalize, map, shareReplay, startWith, switchMap, take } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { formatInvoiceNumber } from '../../shared/invoice';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { CUSTOMER_DATA_SERVICE, CustomerDataService } from '../../customer/customer-data';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';

export interface RefundDialogData {
  payment: Transaction;
}

export const REFUND_ACTION = 'refund';
export const VIEW_INVOICE_ACTION = 'viewInvoice';

interface RefundResult {
  action: typeof REFUND_ACTION;
  result: RetailPayment;
}

interface IssueCreditNoteResult {
  action: typeof VIEW_INVOICE_ACTION;
  result: Invoice;
}

export type RefundDialogResult = RefundResult | IssueCreditNoteResult;

@Component({
  standalone: true,
  selector: 'smb-invoicing-refund-dialog',
  templateUrl: './refund-dialog.component.html',
  styleUrls: ['./refund-dialog.component.scss'],
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    AsyncPipe,
    CurrencyPipe,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    GalaxyAlertModule,
    GalaxyFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    GalaxyButtonLoadingIndicatorModule,
  ],
})
export class RefundDialogComponent implements OnInit {
  formGroup: FormGroup;
  reasonOptions: SelectInputOption[];
  isSubmitting$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  paymentDollarAmount: number;
  currencyCode: string;

  companyName$: Observable<string>;
  balance$: Observable<string>;
  showNegativeBalanceAlert$: Observable<boolean>;

  achDebitPaymentMethodType = RetailPaymentMethodType.ACHDebit;
  acssDebitPaymentMethodType = RetailPaymentMethodType.ACSSDebit;
  cardPaymentMethodType = RetailPaymentMethodType.Card;

  invoice = toSignal(
    of(this.data.payment).pipe(
      switchMap((payment) => {
        if (payment.referenceType === TransactionReferenceType.INVOICE) {
          return this.invoiceService.get(payment.merchantId, payment.referenceId);
        }
        return of(null);
      }),
      map((invoice) => {
        if (invoice) {
          return {
            ...invoice,
            formattedNumber: formatInvoiceNumber(invoice.number),
          };
        }
        return null;
      }),
    ),
  );

  constructor(
    public dialogRef: MatDialogRef<RefundDialogComponent, RefundDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: RefundDialogData,
    @Inject(CUSTOMER_DATA_SERVICE) private customerDataService: CustomerDataService,
    private merchantService: MerchantService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private paymentService: PaymentService,
    private snackbarService: SnackbarService,
    private readonly invoiceService: InvoiceService,
  ) {}

  get amountControl(): FormControl {
    return this.formGroup.get('amount') as FormControl;
  }

  get reasonControl(): FormControl {
    return this.formGroup.get('reason') as FormControl;
  }

  get currencySymbol(): string {
    return getCurrencySymbol(this.currencyCode, 'wide');
  }

  ngOnInit(): void {
    this.paymentDollarAmount = convertCentsToDollars(this.data.payment.amount || 0);
    this.currencyCode = this.data.payment.currencyCode;

    this.reasonOptions = [
      {
        label: this.translateService.instant('REFUND_DIALOG.SELECT_REASON'),
        value: RetailRefundReason.Unset,
      },
      {
        label: this.translateService.instant('REFUND_DIALOG.DUPLICATE'),
        value: RetailRefundReason.Duplicate,
      },
      {
        label: this.translateService.instant('REFUND_DIALOG.FRAUDULENT'),
        value: RetailRefundReason.Fraudulent,
      },
      {
        label: this.translateService.instant('REFUND_DIALOG.REQUESTED_BY_CUSTOMER'),
        value: RetailRefundReason.RequestedByCustomer,
      },
    ];

    this.formGroup = this.formBuilder.group({
      amount: new UntypedFormControl(this.paymentDollarAmount.toFixed(2), {
        validators: [
          Validators.required,
          Validators.max(this.paymentDollarAmount),
          Validator.isGreaterThanZero(),
          Validators.pattern('^[0-9.,-]*$'),
        ],
        updateOn: 'blur',
      }),
      reason: new FormControl<RetailRefundReason>(RetailRefundReason.Unset),
    });

    this.companyName$ = this.customerDataService.get(this.data.payment.customerId).pipe(
      map((customer) => customer?.companyName),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.balance$ = this.merchantService.getRetailAccountBalance(this.data.payment.merchantId).pipe(
      map((accountBalance) => {
        const currencyBalance = (accountBalance?.available || []).find(
          (b) => b.currencyCode.toLowerCase() === this.data.payment.currencyCode.toLowerCase(),
        );
        return (currencyBalance?.amount / 100 || 0).toFixed(0);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const requestedAmount$ = this.amountControl.valueChanges.pipe(startWith(this.paymentDollarAmount));
    this.showNegativeBalanceAlert$ = combineLatest([this.balance$, requestedAmount$]).pipe(
      map(([balance, requestedAmount]) => this.amountControl.valid && Number(balance) < Number(requestedAmount)),
    );

    combineLatest([this.companyName$, this.balance$])
      .pipe(take(1))
      .subscribe(() => this.isLoading$$.next(false));
  }

  public onSubmit(): void {
    this.isSubmitting$$.next(true);
    this.paymentService
      .refundRetail(
        this.data.payment.merchantId,
        this.data.payment.id,
        Math.round(this.amountControl.value * 100), //round to correct for floating point imprecision
        this.reasonControl.value,
      )
      .pipe(
        take(1),
        finalize(() => this.isSubmitting$$.next(false)),
      )
      .subscribe(
        (result) => {
          this.snackbarService.openSuccessSnack(this.translateService.instant('REFUND_DIALOG.PAYMENT_REFUNDED'));
          this.dialogRef.close({ action: REFUND_ACTION, result });
        },
        (err) => {
          return this.snackbarService.openErrorSnack(
            this.translateService.instant('REFUND_DIALOG.FAILED_TO_REFUND', {
              err: err.error?.message || err.message || err,
            }),
          );
        },
      );
  }

  public issueCreditNote(): void {
    const invoice = this.invoice();
    if (!invoice) {
      return;
    }
    this.dialogRef.close({ action: VIEW_INVOICE_ACTION, result: invoice });
  }
}
